import React, { useState, useEffect } from "react";
import { useLocation, Link as RouterLink, NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useQuery, useMutation, gql } from "@apollo/client";
import { useSpace } from "../../../../contexts/SpaceContext";
import styled from "@emotion/styled";

import {
  Alert,
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Button,
  Card,
  CardContent,
  Divider as MuiDivider,
  Grid,
  Link,
  Paper,
  Snackbar,
  Tab,
  Tabs,
  Typography,
  CircularProgress,
} from "@mui/material";

import {
  ArrowBack as ArrowBackIcon,
  SmartToy as BotIcon,
  Settings as SettingsIcon,
  Storage as StorageIcon,
  Psychology as AIIcon,
  SwapHoriz as EscalationIcon,
  Chat as ChatIcon,
} from "@mui/icons-material";

import { spacing } from "@mui/system";

// Import panel components
import BotSettingsPanel from "./components/BotSettingsPanel";
import KnowledgeBasePanel from "./components/KnowledgeBasePanel";
import AIConfigPanel from "./components/AIConfigPanel";
import EscalationConfigPanel from "./components/EscalationConfigPanel";
import TestChatPanel from "./components/TestChatPanel";

const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

// GraphQL Queries
const GET_WIDGET = gql`
  query GetWidget($id: ID!) {
    getWidget(id: $id) {
      id
      business_details {
        name
        display_name
        website_url
      }
      script_id
    }
  }
`;

const GET_BOT_SETTINGS = gql`
  query GetBotSettings($widgetId: ID!) {
    getBotSettings(widgetId: $widgetId) {
      enabled
      greetingMessage
      personality
      ragEnabled
      aiConfig {
        provider
        model
        temperature
        maxTokens
      }
      ragConfig {
        topK
        minRelevanceScore
        includeSourceCitations
      }
      escalationConfig {
        escalateOnNoMatch
        escalateAfterTurns
        escalationMessage
        handoffTriggerKeywords
        autoHandoffOnNegativeSentiment
        sentimentThreshold
      }
    }
  }
`;

const GET_BOT_KNOWLEDGE_BASE = gql`
  query GetBotKnowledgeBase($widgetId: ID!) {
    getBotKnowledgeBase(widgetId: $widgetId) {
      id
      widgetId
      sources {
        websiteUrls {
          url
          crawlDepth
          crawlStatus
          pagesCrawled
          lastCrawledAt
        }
        documentationUrls {
          url
          title
          fetchStatus
          lastFetchedAt
        }
        uploadedFiles {
          fileId
          originalName
          mimeType
          fileSize
          processStatus
          chunksGenerated
        }
      }
      indexingStatus {
        status
        lastIndexedAt
        totalChunks
        totalDocuments
        indexingProgress
      }
      stats {
        totalQueriesAnswered
        averageRelevanceScore
        lastQueryAt
      }
    }
  }
`;

// Tab panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`bot-config-tabpanel-${index}`}
      aria-labelledby={`bot-config-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `bot-config-tab-${index}`,
    "aria-controls": `bot-config-tabpanel-${index}`,
  };
}

function BotConfigurationPage() {
  const location = useLocation();
  const { currentSpace } = useSpace();

  // Get URL parameters
  const queryParams = new URLSearchParams(location.search);
  const widgetId = queryParams.get("widgetId");
  const spaceId = queryParams.get("spaceId") || currentSpace?.id;

  // State
  const [tabValue, setTabValue] = useState(0);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // Fetch widget data
  const {
    loading: widgetLoading,
    error: widgetError,
    data: widgetData,
  } = useQuery(GET_WIDGET, {
    variables: { id: widgetId },
    skip: !widgetId,
  });

  // Fetch bot settings
  const {
    loading: settingsLoading,
    error: settingsError,
    data: settingsData,
    refetch: refetchSettings,
  } = useQuery(GET_BOT_SETTINGS, {
    variables: { widgetId },
    skip: !widgetId,
    fetchPolicy: "cache-and-network",
  });

  // Fetch knowledge base
  const {
    loading: kbLoading,
    error: kbError,
    data: kbData,
    refetch: refetchKnowledgeBase,
  } = useQuery(GET_BOT_KNOWLEDGE_BASE, {
    variables: { widgetId },
    skip: !widgetId,
    fetchPolicy: "cache-and-network",
  });

  // Poll for crawl status updates when URLs are being crawled
  useEffect(() => {
    const websiteUrls = kbData?.getBotKnowledgeBase?.sources?.websiteUrls || [];
    const hasPendingCrawls = websiteUrls.some(
      (site) =>
        site.crawlStatus === "crawling" || site.crawlStatus === "pending"
    );

    let pollInterval;
    if (hasPendingCrawls && !kbLoading) {
      console.log("🔄 Starting crawl status polling...");
      pollInterval = setInterval(() => {
        console.log("🔄 Polling for crawl status update...");
        refetchKnowledgeBase();
      }, 3000); // Poll every 3 seconds
    }

    return () => {
      if (pollInterval) {
        console.log("✅ Stopped crawl status polling");
        clearInterval(pollInterval);
      }
    };
  }, [kbData, kbLoading, refetchKnowledgeBase]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  const showAlert = (message, severity = "info") => {
    setAlert({ open: true, message, severity });
  };

  // Handle no widget ID
  if (!widgetId) {
    return (
      <Paper sx={{ p: 3, textAlign: "center" }}>
        <Typography color="error">
          No widget selected. Please select a widget to configure its AI Bot.
        </Typography>
        <Button
          component={RouterLink}
          to={`/widget/channel-connect${spaceId ? `?spaceId=${spaceId}` : ""}`}
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Back to Widgets
        </Button>
      </Paper>
    );
  }

  // Handle loading state
  if (widgetLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Handle error state
  if (widgetError) {
    return (
      <Paper sx={{ p: 3, textAlign: "center" }}>
        <Typography color="error">
          Error loading widget: {widgetError.message}
        </Typography>
        <Button
          component={RouterLink}
          to={`/widget/channel-connect${spaceId ? `?spaceId=${spaceId}` : ""}`}
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Back to Widgets
        </Button>
      </Paper>
    );
  }

  const widget = widgetData?.getWidget;
  const botSettings = settingsData?.getBotSettings;
  const knowledgeBase = kbData?.getBotKnowledgeBase;

  return (
    <React.Fragment>
      <Helmet title="AI Bot Configuration" />

      {/* Header */}
      <Grid justifyContent="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            <BotIcon sx={{ mr: 1, verticalAlign: "middle" }} />
            AI Bot Configuration
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to={`/spaces/${spaceId}`}>
              {currentSpace?.name || "Spaces"}
            </Link>
            <Link
              component={NavLink}
              to={`/widget/channel-connect?spaceId=${spaceId}`}
            >
              Channel Connect
            </Link>
            <Typography>
              {widget?.business_details?.display_name ||
                widget?.business_details?.name ||
                "AI Bot Configuration"}
            </Typography>
          </Breadcrumbs>
        </Grid>
        <Grid item>
          <Button
            component={RouterLink}
            to={`/widget/channel-connect?spaceId=${spaceId}`}
            startIcon={<ArrowBackIcon />}
            variant="outlined"
          >
            Back to Widgets
          </Button>
        </Grid>
      </Grid>

      <Divider my={6} />

      {/* Widget Info Card */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <BotIcon sx={{ fontSize: 48, color: "primary.main" }} />
            </Grid>
            <Grid item xs>
              <Typography variant="h5">
                {widget?.business_details?.display_name ||
                  widget?.business_details?.name}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Widget ID: {widgetId}
              </Typography>
              {widget?.business_details?.website_url && (
                <Typography variant="body2" color="textSecondary">
                  Website: {widget.business_details.website_url}
                </Typography>
              )}
            </Grid>
            <Grid item>
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  backgroundColor: botSettings?.enabled
                    ? "success.light"
                    : "grey.200",
                  color: botSettings?.enabled
                    ? "success.contrastText"
                    : "text.secondary",
                }}
              >
                <Typography variant="subtitle2">
                  {botSettings?.enabled ? "Bot Enabled" : "Bot Disabled"}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Configuration Tabs */}
      <Paper sx={{ width: "100%" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="AI Bot configuration tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              icon={<SettingsIcon />}
              iconPosition="start"
              label="Bot Settings"
              {...a11yProps(0)}
            />
            <Tab
              icon={<StorageIcon />}
              iconPosition="start"
              label="Knowledge Base"
              {...a11yProps(1)}
            />
            <Tab
              icon={<AIIcon />}
              iconPosition="start"
              label="AI Configuration"
              {...a11yProps(2)}
            />
            <Tab
              icon={<EscalationIcon />}
              iconPosition="start"
              label="Escalation Rules"
              {...a11yProps(3)}
            />
            <Tab
              icon={<ChatIcon />}
              iconPosition="start"
              label="Test Chat"
              {...a11yProps(4)}
            />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <Box sx={{ p: 3 }}>
          <TabPanel value={tabValue} index={0}>
            <BotSettingsPanel
              widgetId={widgetId}
              botSettings={botSettings}
              loading={settingsLoading}
              onRefetch={refetchSettings}
              showAlert={showAlert}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <KnowledgeBasePanel
              widgetId={widgetId}
              knowledgeBase={knowledgeBase}
              loading={kbLoading}
              onRefetch={refetchKnowledgeBase}
              showAlert={showAlert}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <AIConfigPanel
              widgetId={widgetId}
              botSettings={botSettings}
              loading={settingsLoading}
              onRefetch={refetchSettings}
              showAlert={showAlert}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <EscalationConfigPanel
              widgetId={widgetId}
              botSettings={botSettings}
              loading={settingsLoading}
              onRefetch={refetchSettings}
              showAlert={showAlert}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <TestChatPanel
              widgetId={widgetId}
              botSettings={botSettings}
              showAlert={showAlert}
            />
          </TabPanel>
        </Box>
      </Paper>

      {/* Alert Snackbar */}
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
      >
        <Alert onClose={handleCloseAlert} severity={alert.severity}>
          {alert.message}
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
}

export default BotConfigurationPage;
